<script setup lang="ts">
import {useData} from "vike-vue/useData";
import {computed, ref, watch} from "vue";
import {Find, useWebPopup, WebForm, WebInput, InsertOne} from "lct-lib";
import {IOrderForm} from "../../../core/interface/IOrderForm";
import {useCartStore} from "../cart.store";
import _ from "lodash";
import getTeamName from "../../../core/utils/teamName";
import ProductDesign from "../../../core/cpn/web/banner/ProductDesign.vue";
import UploadImage from "../../design-tool/libs/UploadImage.vue";

const {show} = useWebPopup();
const data: any = useData();
const {addItem} = useCartStore();
const quantity = ref(1);

const orderForm = ref<IOrderForm>({
  note: "",
  designAssets: [],
  players: [
    {name: "", number: ""},
    {name: "", number: ""},
    {name: "", number: ""}
  ]
});

const similarList: any = ref([]);

async function getSimilarList() {
  try {
    let keyword = data.banner.label.replace("Banner", "").replace("banner", "");
    keyword = keyword.replace(/\d+/gi, "");
    const result = await Find(
      "designs",
      {$text: {$search: _.trim(keyword)}, type: data.banner.type, editable: true},
      {limit: 150},
      "teambannersports_com"
    );
    similarList.value = result.docs || [];
  } catch (e) {
    console.error(e);
  }
}

watch(
  () => data.banner.label,
  async () => {
    await getSimilarList();
  },
  {immediate: true}
);

const back = () => {
  window.history.back();
};

const newPlayer = () => {
  orderForm.value?.players?.push({name: "", number: ""});
};

const removePlayer = (index: number) => {
  orderForm.value.players?.splice(index, 1);
};

const onAssetUploaded = (url: string) => {
  if (!url) return;
  if (!orderForm.value.designAssets) orderForm.value.designAssets = [];
  if (!orderForm.value.designAssets.includes(url)) {
    orderForm.value.designAssets.push(url);
  }
};

const removeAsset = (index: number) => {
  orderForm.value.designAssets?.splice(index, 1);
};

const addToCart = () => {
  const qty = Math.max(1, Number(quantity.value) || 1);
  quantity.value = qty;
  const {designAssets, ...orderFormData} = orderForm.value;
  const item = {
    ...data.banner,
    item_type: "order_form",
    form_data: {
      ...orderFormData,
      "reference assets": designAssets?.join(", ") || ""
    },
    qty
  };
  addItem(item);
  show({
    text: "You have added it to your cart.",
    buttons: [
      {text: "Ok"},
      {
        text: "View Cart",
        handler: () => {
          location.href = "/cart";
        },
        color: "green"
      }
    ]
  });
};

const linkMore = computed(() => {
  if (data.banner.label.includes("Soccer") || data.banner.label.includes("soccer")) {
    if (data.banner.type === "hem-grommet") return "/soccer-banners/hem-grommets-soccer-banners";
    if (data.banner.type === "pole-pocket") return "/soccer-banners/pole-pocket-soccer-banners";
    if (data.banner.type === "triangle") return "/soccer-banners/triangle-soccer-banners";
    if (data.banner.type === "home-plate") return "/soccer-banners/home-plate-soccer-banners";
  }
  return "";
});

const salePrice = computed(() => {
  if (data.banner.type === "triangle" || data.banner.type === "home-plate") return "9.99";
  return data.banner.sale_price;
});

const customerReviewCollection = "customer_reviews";
const customerReviews: any = ref([]);
const REVIEWS_PER_PAGE = 9;
const currentReviewPage = ref(1);

const totalReviewPages = computed(() => {
  return Math.max(1, Math.ceil((customerReviews.value?.length || 0) / REVIEWS_PER_PAGE));
});

const paginatedReviews = computed(() => {
  const start = (currentReviewPage.value - 1) * REVIEWS_PER_PAGE;
  const end = start + REVIEWS_PER_PAGE;
  return (customerReviews.value || []).slice(start, end);
});

const reviewPageNumbers = computed(() => {
  return Array.from({length: totalReviewPages.value}, (_, i) => i + 1);
});

async function getCustomerReviews() {
  try {
    const result = await Find(
      customerReviewCollection,
      {},
      {sort: {created_at: -1}, limit: 100},
      "teambannersports_com"
    );
    customerReviews.value = result?.docs || [];
  } catch (e) {
    console.error(e);
    customerReviews.value = [];
  }
}

watch(
  () => data.banner?._id,
  async () => {
    if (!data.banner?._id) return;
    currentReviewPage.value = 1;
    await getCustomerReviews();
  },
  {immediate: true}
);

watch(
  () => customerReviews.value.length,
  () => {
    if (currentReviewPage.value > totalReviewPages.value) {
      currentReviewPage.value = totalReviewPages.value;
    }
  }
);

const showReviewForm = ref(false);
const isSubmittingReview = ref(false);
const newReview = ref({
  name: "",
  email: "",
  team: "",
  rating: 5,
  comment: ""
});

const toggleReviewForm = () => {
  showReviewForm.value = !showReviewForm.value;
};

const goToReviewPage = (page: number) => {
  if (page < 1 || page > totalReviewPages.value) return;
  currentReviewPage.value = page;
};

const submitReview = async () => {
  if (isSubmittingReview.value) return;

  const name = newReview.value.name.trim();
  const email = newReview.value.email.trim();
  const team = newReview.value.team.trim();
  const comment = newReview.value.comment.trim();
  const rating = Math.min(5, Math.max(1, Number(newReview.value.rating) || 5));
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!name || !email || !team || !comment) {
    show({text: "Please fill in your name, email, team name, and review comment."});
    return;
  }

  if (!emailRegex.test(email)) {
    show({text: "Please enter a valid email address."});
    return;
  }

  isSubmittingReview.value = true;
  try {
    const reviewDoc = {
      name,
      email,
      team,
      rating,
      comment,
      banner_id: data.banner?._id,
      banner_label: data.banner?.label || "",
      created_at: new Date().toISOString()
    };

    await InsertOne(customerReviewCollection, reviewDoc);
    await getCustomerReviews();
    currentReviewPage.value = 1;

    newReview.value = {
      name: "",
      email: "",
      team: "",
      rating: 5,
      comment: ""
    };
    showReviewForm.value = false;
    show({text: "Thank you! Your review has been submitted."});
  } catch (e) {
    console.error(e);
    show({text: "Unable to submit your review right now. Please try again."});
  } finally {
    isSubmittingReview.value = false;
  }
};
</script>

<template>
  <div c2c-0 class="team-banner-page">
    <div c2c-1 class="back-wrap">
      <a c2c-2 class="back-link" @click="back">
        <span c2c-3 class="icon-left-big"></span>
        Back
      </a>
    </div>

    <section c2c-4 class="hero-card">
      <div c2c-5 class="preview-col">
        <div c2c-6 class="banner-media">
          <div v-if="data.banner.type === 'hem-grommet'" c2c-7 class="mockup">
            <img c2c-8 class="banner" :src="data.banner.img_url || data.banner.jpg_url" :alt="data.banner.alt">
            <img c2c-9 class="frame" src="/proof/Proof-Hem-Grommet.webp" alt="proof hem grommet">
          </div>

          <a v-else :href="`/design-tool/?u=${data.banner.svg_url}`">
            <img c2c-10 class="main-image" :src="data.banner.img_url || data.banner.jpg_url" :alt="data.banner.alt">
          </a>
        </div>

        <h1>{{ data.banner.label }}</h1>

        <div c2c-11 class="preview-actions">
          <a c2c-12 class="design-btn" :href="`/design-tool/?u=${data.banner.svg_url}`">
            <img height="24" src="/icon/paint.webp" alt="icon paint">
            <span>Design It Yourself</span>
          </a>
        </div>

        <div c2c-13 class="info-html" v-html="data.banner.information"></div>

        <p c2c-14 class="tag-line">
          <span>Tags:</span>
          <a :href="`/search/?q=${getTeamName(data.banner.label)}`">{{ getTeamName(data.banner.label) }}</a>
        </p>

        <p c2c-15 class="stats-line">
          View: {{ data.banner.views }} / Sold: {{ data.banner.num_purchases }}
        </p>
      </div>

      <aside c2c-16 class="form-col">
        <h2>Order Form</h2>
        <p c2c-17 class="form-desc">Provide your team details and we will prepare the design for you.</p>

        <web-form c2c-18 class="order-form">
          <web-input type="text" label="Team name" label-style="col" v-model="orderForm.teamName"></web-input>
          <web-input type="text" label="Team Manager(s)" label-style="col" v-model="orderForm.teamManager"></web-input>
          <web-input type="text" label="Asst. Manager(s)" label-style="col" v-model="orderForm.asstManager"></web-input>
          <web-input type="text" label="Coaches" label-style="col" v-model="orderForm.coach"></web-input>
          <web-input type="text" label="Team Mom/Dad/Parent" label-style="col" v-model="orderForm.teamParent"></web-input>
          <web-input type="text" label="Team Sponsor(s)" label-style="col" v-model="orderForm.teamSponsor"></web-input>
          <web-input type="text" label="Organization Information" label-style="col" v-model="orderForm.organizationInfo"></web-input>
          <div c2c-19 class="order-note">
            <label for="order-note-textarea">Note</label>
            <textarea
              id="order-note-textarea"
              v-model="orderForm.note"
              rows="4"
              placeholder="Enter any extra instructions for your order..."
            ></textarea>
          </div>

          <div c2c-20 class="asset-upload">
            <p c2c-21 class="asset-title">Reference Images / Assets</p>
            <p c2c-22 class="asset-desc">Upload sample images, logos, or files to support your design request.</p>
            <div c2c-23 class="asset-upload-box">
              <upload-image :multiple="false" @uploaded="onAssetUploaded"></upload-image>
              <p c2c-24 class="asset-hint">JPG, PNG, SVG, or GIF</p>
            </div>
            <div c2c-25 class="asset-grid" v-if="orderForm.designAssets?.length">
              <div c2c-26 class="asset-item" v-for="(assetUrl, assetIndex) in orderForm.designAssets" :key="assetUrl">
                <img :src="assetUrl" alt="Uploaded design asset preview">
                <button type="button" c2c-27 class="remove-asset" @click="removeAsset(assetIndex)">Remove</button>
              </div>
            </div>
          </div>

          <div c2c-28 class="player-box">
            <div c2c-29 class="player-head">
              <p>Player Name ({{ orderForm?.players?.length }})</p>
              <p>Number</p>
            </div>
            <div c2c-30 class="player-row" v-for="(player, index) in orderForm.players" :key="index">
              <input type="text" c2c-31 class="input-name" v-model="player.name" placeholder="The name of the player">
              <input type="text" c2c-32 class="input-number" v-model="player.number" placeholder="No.">
              <button type="button" c2c-33 class="remove-player icon-cancel" @click="removePlayer(index)"></button>
            </div>
            <button type="button" c2c-34 class="new-player" @click="newPlayer">+ New Player</button>
          </div>

          <div c2c-35 class="price-box">
            <p c2c-36 class="price-line">
              <span>Price:</span>
              <span c2c-37 class="sale-price">${{ salePrice }}</span>
              <span c2c-38 class="origin-price">${{ data.banner.price }}</span>
            </p>
            <p
              v-if="data.banner.type === 'hem-grommet' || data.banner.type === 'pole-pocket'"
              c2c-39 class="free-mini"
            >
              + FREE Mini Banners for Each Player
            </p>
          </div>

          <div c2c-40 class="qty-box">
            <label for="order-qty">Quantity</label>
            <input id="order-qty" v-model.number="quantity" type="number" min="1" step="1">
          </div>

          <button type="button" c2c-41 class="add-cart-btn" @click="addToCart">
            <span c2c-42 class="icon-basket"></span>
            + Add To Cart
          </button>

          <p c2c-43 class="or-line">or</p>

          <a c2c-44 class="secondary-btn" :href="`/design-tool/?u=${data.banner.svg_url}`">
            <img height="24" src="/icon/paint.webp" alt="icon paint">
            <span>Design It Yourself</span>
          </a>
        </web-form>
      </aside>
    </section>

    <section c2c-45 class="similar-section">
      <div c2c-46 class="review-section">
        <div c2c-47 class="section-head">
          <h2>Customer Reviews</h2>
          <p>{{ customerReviews.length }} reviews from team managers and parents.</p>
        </div>

        <div c2c-48 class="review-action">
          <button type="button" c2c-49 class="write-review-btn" @click="toggleReviewForm">
            {{ showReviewForm ? "Close Review Form" : "Write a Review" }}
          </button>
        </div>

        <form v-if="showReviewForm" c2c-50 class="review-form" @submit.prevent="submitReview">
          <input v-model="newReview.name" type="text" placeholder="Your name" required>
          <input v-model="newReview.email" type="email" placeholder="Your email" required>
          <input v-model="newReview.team" type="text" placeholder="Team name" required>
          <select v-model.number="newReview.rating">
            <option :value="5">5 Stars</option>
            <option :value="4">4 Stars</option>
            <option :value="3">3 Stars</option>
            <option :value="2">2 Stars</option>
            <option :value="1">1 Star</option>
          </select>
          <textarea v-model="newReview.comment" rows="4" placeholder="Write your review..." required></textarea>
          <button type="submit" c2c-51 class="submit-review-btn" :disabled="isSubmittingReview">
            {{ isSubmittingReview ? "Submitting..." : "Submit Review" }}
          </button>
        </form>

        <ul c2c-52 class="review-grid">
          <li v-for="(review, index) in paginatedReviews" :key="`${review.name}-${review.created_at || index}`" c2c-53 class="review-card">
            <p c2c-54 class="review-stars">{{ "★".repeat(review.rating) }}</p>
            <p c2c-55 class="review-comment">"{{ review.comment }}"</p>
            <p c2c-56 class="review-author">{{ review.name }} - {{ review.team }}</p>
          </li>
        </ul>

        <div c2c-57 class="review-pagination" v-if="totalReviewPages > 1">
          <button
            type="button"
            c2c-58 class="review-page-btn"
            :disabled="currentReviewPage === 1"
            @click="goToReviewPage(currentReviewPage - 1)"
          >
            Previous
          </button>
          <button
            v-for="page in reviewPageNumbers"
            :key="page"
            type="button"
            c2c-59 class="review-page-btn"
            :class="{ active: page === currentReviewPage }"
            @click="goToReviewPage(page)"
          >
            {{ page }}
          </button>
          <button
            type="button"
            c2c-60 class="review-page-btn"
            :disabled="currentReviewPage === totalReviewPages"
            @click="goToReviewPage(currentReviewPage + 1)"
          >
            Next
          </button>
        </div>
      </div>

      <div c2c-61 class="section-head">
        <h2>Similar Designs</h2>
        <p>Explore more templates in this style.</p>
      </div>

      <ul c2c-62 class="similar-grid">
        <li v-for="item in similarList" :key="item._id">
          <ProductDesign :design="item"></ProductDesign>
        </li>
      </ul>

      <div c2c-63 class="more-wrap" v-if="linkMore">
        <a c2c-64 class="more-link" :href="linkMore">View more designs</a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.team-banner-page {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 16px 14px 44px;
  color: #0f172a;
}

.back-wrap {
  margin-bottom: 12px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #334155;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.back-link:hover {
  color: #0f172a;
}

.hero-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  padding: 18px;
  display: grid;
  grid-template-columns: 1fr 430px;
  gap: 20px;
}

.preview-col {
  min-width: 0;
}

.banner-media {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
}

.main-image {
  display: block;
  width: 100%;
}

h1 {
  margin: 14px 0 0;
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1.2;
}

.preview-actions {
  margin-top: 14px;
}

.design-btn,
.secondary-btn {
  width: fit-content;
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid #16a34a;
  color: #166534;
  background: #f0fdf4;
  text-decoration: none;
  font-weight: 800;
}

.design-btn:hover,
.secondary-btn:hover {
  border-color: #22c55e;
  background: #dcfce7;
}

.info-html {
  margin-top: 14px;
  color: #334155;
  line-height: 1.65;
}

.tag-line,
.stats-line {
  margin: 12px 0 0;
  color: #475569;
  font-size: 14px;
}

.tag-line span {
  font-weight: 700;
}

.tag-line a {
  margin-left: 6px;
  color: #0f766e;
  text-decoration: underline;
}

.stats-line {
  font-style: italic;
}

.form-col {
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background:
    radial-gradient(150% 160% at 100% 0%, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0) 55%),
    linear-gradient(180deg, #f8fbff 0%, #f1f7ff 100%);
  padding: 16px;
}

.form-col h2 {
  margin: 0;
  font-size: 24px;
}

.form-desc {
  margin: 8px 0 0;
  color: #475569;
  font-size: 14px;
}

.order-form {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-form > web-input {
  display: block;
  margin-bottom: 0;
}

.order-form > web-input:last-of-type {
  margin-bottom: 4px;
}

.order-note {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}

.order-note label {
  color: #334155;
  font-weight: 700;
  font-size: 14px;
}

.order-note textarea {
  min-height: 96px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 10px;
  font: inherit;
  resize: vertical;
}

.asset-upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.asset-title {
  margin: 0;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.asset-desc {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.asset-upload-box {
  display: flex;
  align-items: center;
  gap: 10px;
}

.asset-hint {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.asset-item {
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #ffffff;
  padding: 8px;
}

.asset-item img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.remove-asset {
  margin-top: 6px;
  width: 100%;
  min-height: 30px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  font-weight: 700;
  cursor: pointer;
}

.player-box {
  margin-top: 12px;
}

.player-head {
  display: grid;
  grid-template-columns: 1fr 90px;
  gap: 8px;
  font-weight: 800;
  font-size: 15px;
}

.player-head p {
  margin: 0 0 8px;
}

.player-row {
  display: grid;
  grid-template-columns: 1fr 90px 34px;
  gap: 8px;
  margin-bottom: 8px;
}

.input-name,
.input-number {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
}

.input-number {
  text-align: center;
}

.remove-player {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.remove-player::before {
  line-height: 1;
  font-size: 16px;
}

.new-player {
  margin-top: 4px;
  border: none;
  background: none;
  color: #0f766e;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
}

.price-box {
  margin-top: 14px;
}

.price-line {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sale-price {
  color: #dc2626;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.origin-price {
  color: #64748b;
  text-decoration: line-through;
  font-size: 18px;
  font-weight: 700;
}

.free-mini {
  margin: 6px 0 0;
  color: #15803d;
  font-weight: 800;
}

.qty-box {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.qty-box label {
  color: #334155;
  font-weight: 700;
  font-size: 14px;
}

.qty-box input {
  width: 90px;
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
  font: inherit;
}

.add-cart-btn {
  margin-top: 14px;
  width: 100%;
  min-height: 44px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(90deg, #0b5ad9 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 800;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.add-cart-btn:hover {
  filter: brightness(1.05);
}

.or-line {
  text-align: center;
  color: #64748b;
  margin: 10px 0;
  font-style: italic;
}

.secondary-btn {
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
}

.similar-section {
  margin-top: 28px;
}

.review-section {
  margin-bottom: 26px;
}

.review-grid {
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.review-action {
  margin-top: 14px;
}

.write-review-btn {
  border: 1px solid #2563eb;
  border-radius: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 800;
  padding: 9px 14px;
  cursor: pointer;
}

.write-review-btn:hover {
  border-color: #1d4ed8;
  background: #dbeafe;
}

.review-form {
  margin-top: 14px;
  display: grid;
  gap: 10px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 12px;
  background: #f8fbff;
}

.review-form input,
.review-form select,
.review-form textarea {
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
}

.review-form textarea {
  min-height: 100px;
  resize: vertical;
}

.submit-review-btn {
  width: fit-content;
  min-height: 40px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(90deg, #0b5ad9 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 800;
  padding: 8px 14px;
  cursor: pointer;
}

.submit-review-btn:hover {
  filter: brightness(1.05);
}

.submit-review-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: none;
}

.review-card {
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 14px;
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 55%),
    #ffffff;
}

.review-stars {
  margin: 0;
  color: #f59e0b;
  letter-spacing: 2px;
  font-size: 18px;
}

.review-comment {
  margin: 8px 0 0;
  color: #1e293b;
  line-height: 1.55;
}

.review-author {
  margin: 10px 0 0;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
}

.review-pagination {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.review-page-btn {
  min-width: 38px;
  min-height: 36px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-weight: 700;
  padding: 0 10px;
  cursor: pointer;
}

.review-page-btn:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.review-page-btn.active {
  border-color: #1d4ed8;
  background: #dbeafe;
  color: #1e3a8a;
}

.review-page-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.section-head h2 {
  margin: 0;
  font-size: clamp(22px, 3vw, 30px);
}

.section-head p {
  margin: 8px 0 0;
  color: #475569;
}

.similar-grid {
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.more-wrap {
  margin-top: 16px;
  text-align: center;
}

.more-link {
  display: inline-block;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #93c5fd;
  color: #0b5ad9;
  background: #eff6ff;
  text-decoration: none;
  font-weight: 800;
}

.more-link:hover {
  border-color: #60a5fa;
}

.mockup {
  position: relative;
  width: 100%;
  max-width: 1024px;
  aspect-ratio: 1024 / 669;
  overflow: hidden;
}

.mockup .banner {
  position: absolute;
  left: 3%;
  top: 8%;
  width: 91.3%;
  aspect-ratio: 760 / 454;
  z-index: 1;
}

.mockup .frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 2;
  pointer-events: none;
}

@media (max-width: 1150px) {
  .hero-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1100px) {
  .review-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .similar-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .similar-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .team-banner-page {
    padding: 12px 10px 36px;
  }

  .hero-card {
    padding: 12px;
  }

  .player-head {
    grid-template-columns: 1fr 70px;
  }

  .player-row {
    grid-template-columns: 1fr 70px 32px;
    gap: 6px;
  }

  .review-grid {
    grid-template-columns: 1fr;
  }

  .similar-grid {
    grid-template-columns: 1fr;
  }
}
</style>
<style lang="scss" scoped></style>